import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/src/ReactCrop.scss';
import { canvasPreview } from './canvasPreview';

type ImageCropToolProps = {
	src: string;
	onComplete: (img: string | null) => void;
};

export interface ImageCropToolRef {
	generateCroppedImage: () => string | null;
}

export const ImageCropTool = forwardRef<ImageCropToolRef, ImageCropToolProps>(({ src, onComplete = () => null }, ref) => {
	const [crop, setCrop] = useState({
		unit: 'px' as const,
		x: 40,
		y: 20,
		width: 200,
		height: 130
	});

	const [completedCrop, setCompletedCrop] = useState({
		unit: 'px' as const,
		x: 40,
		y: 20,
		width: 200,
		height: 130
	});

	const [imageLoaded, setImageLoaded] = useState(false);
	const previewCanvasRef = useRef<HTMLCanvasElement>(null);
	const imgRef = useRef<HTMLImageElement>(null);

	const handleImageLoad = () => {
		setImageLoaded(true);
	};

	const generateCroppedImage = () => {
		if (completedCrop?.width && completedCrop?.height && previewCanvasRef?.current && imgRef?.current && imageLoaded) {
			try {
				canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, 1, 0);

				const canvas = previewCanvasRef.current;
				const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
				onComplete(dataUrl);
				return dataUrl;
			} catch (error) {
				console.error('Canvas export error:', error);
				onComplete(null);
				return null;
			}
		}
		return null;
	};

	useEffect(() => {
		if (completedCrop?.width && completedCrop?.height && previewCanvasRef?.current && imgRef?.current && imageLoaded) {
			canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, 1, 0);
		}
	}, [completedCrop, imageLoaded]);

	useImperativeHandle(ref, () => ({
		generateCroppedImage
	}));

	useEffect(() => {
		if (src) {
			fetch(src, { cache: 'no-cache' })
				.then(response => response.blob())
				.then(blob => {
					const imgElement = document.getElementById('crop-image');
					const imgURL = URL.createObjectURL(blob);
					imgElement.src = imgURL;
				})
				.catch(error => console.error('Error fetching image:', error));
		}
	}, [src]);

	return (
		<>
			<div
				style={{
					width: '100%',
					height: 'auto',
					maxHeight: '495px',
					position: 'relative',
					zIndex: 100
				}}>
				<ReactCrop
					crop={crop}
					onChange={cropped => setCrop(cropped)}
					onComplete={c => setCompletedCrop(c)}>
					<img
						ref={imgRef}
						alt="crop"
						id="crop-image"
						className="crop-image"
						onLoad={handleImageLoad}
					/>
				</ReactCrop>
			</div>

			<canvas
				ref={previewCanvasRef}
				style={{
					display: 'none'
				}}
			/>
		</>
	);
});
